import React, { useMemo } from 'react';
import { useTasks } from '../../context/TasksContext';
import { useLeads } from '../../context/LeadsContext';
import { useRole } from '../../context/RoleContext';
import { useAuth } from '../../context/AuthContext';
import { avatarColor } from '../inbox/avatarUtils';
import { isBoardVisibleTask } from '../../utils/buildForecast';

const STATUS_COLORS = {
  scheduled: '#3B82F6',
  in_progress: '#EAB308',
  completed: '#22C55E',
};

const MonthSchedulerView = ({ anchorDate, setAnchorDate, setView }) => {
  const { tasks } = useTasks();
  const { leads } = useLeads();
  const { isTechnician } = useRole();
  const { user } = useAuth();

  const monthMatrix = useMemo(() => {
    const year = anchorDate.getFullYear();
    const month = anchorDate.getMonth();

    const firstOfMonth = new Date(year, month, 1);
    const startDay = firstOfMonth.getDay(); // 0-6

    const startDate = new Date(year, month, 1 - startDay);
    startDate.setHours(0, 0, 0, 0);

    const weeks = [];
    for (let week = 0; week < 6; week += 1) {
      const days = [];
      for (let day = 0; day < 7; day += 1) {
        const d = new Date(startDate);
        d.setDate(startDate.getDate() + week * 7 + day);
        days.push(d);
      }
      weeks.push(days);
    }

    return weeks;
  }, [anchorDate]);

  // Helper to create a local date key (YYYY-MM-DD) without UTC drift
  const toLocalDateKey = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const jobsByDayKey = useMemo(() => {
    const map = new Map();
    if (!tasks) return map;

    let jobsFiltered = tasks.filter(
      (task) => task.type === 'job' && task.scheduledStart && isBoardVisibleTask(task),
    );
    // Technicians see only their own assigned jobs
    if (isTechnician && user?.uid) {
      jobsFiltered = jobsFiltered.filter((task) => task.assignedTo === user.uid);
    }
    const jobsValidDate = jobsFiltered.filter((task) => {
      const d = new Date(task.scheduledStart);
      return !Number.isNaN(d.getTime());
    });

    jobsValidDate.forEach((task) => {
      const start = new Date(task.scheduledStart);
      // Use local date components to avoid UTC drift
      const key = toLocalDateKey(start);
      const arr = map.get(key) || [];
      arr.push(task);
      map.set(key, arr);
    });

    if (process.env.NODE_ENV !== 'production') {
      console.debug('[Month Debug] jobsFiltered:', jobsFiltered.length, 'jobsValidDate:', jobsValidDate.length);
    }

    return map;
  }, [tasks, isTechnician, user?.uid]);

  const isSameDay = (d1, d2) =>
    d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth() && d1.getDate() === d2.getDate();

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  return (
    <div className="flex text-xs gap-4 mt-4 h-[calc(100vh-220px)]">
      {/* Left: Month grid */}
      <div className="flex-1 bg-white border border-gray-200 rounded-md flex flex-col">
        <div className="grid grid-cols-7 border-b border-gray-200 text-[11px] font-semibold text-gray-600 bg-gray-50">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((label) => (
            <div key={label} className="px-2 py-2 text-center">
              {label}
            </div>
          ))}
        </div>
        <div className="flex-1 grid grid-cols-7 grid-rows-6 text-[11px]">
          {monthMatrix.flat().map((date) => {
            const inMonth = date.getMonth() === anchorDate.getMonth();
            const key = toLocalDateKey(date);
            const jobs = jobsByDayKey.get(key) || [];
            const isToday = isSameDay(date, today);

            return (
              <button
                key={key}
                type="button"
                className={`border border-gray-100 px-1.5 py-1.5 text-left align-top hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-inset focus:ring-[var(--cb-orange)] box-border overflow-hidden ${
                  inMonth ? 'bg-white' : 'bg-gray-50 text-gray-400'
                } ${isToday ? 'ring-1 ring-inset ring-[var(--cb-orange)]' : ''}`}
                onClick={() => {
                  setAnchorDate(date);
                  setView('day');
                }}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[11px] font-medium">{date.getDate()}</span>
                  {jobs.length > 2 && (
                    <span className="text-[9px] text-gray-400">+{jobs.length - 2}</span>
                  )}
                </div>

                {/* Job chips — service + customer name, max 2 visible */}
                {jobs.slice(0, 2).map((task) => {
                  const lead = leads?.find(l => l.id === task.leadId);
                  const serviceName = task.title || lead?.service || 'Job';
                  const customerName = lead?.name || '';
                  const assignedName = task.assignedToName || '';
                  const techColor = assignedName ? avatarColor(assignedName) : null;
                  const chipBg = techColor ? `${techColor}14` : '#F9FAFB';
                  const chipBorder = techColor || (STATUS_COLORS[task.status] || STATUS_COLORS.scheduled);
                  return (
                    <div
                      key={task.id}
                      className="text-[9px] leading-tight truncate px-1 py-0.5 rounded mb-0.5"
                      style={{ backgroundColor: chipBg, borderLeft: `2px solid ${chipBorder}` }}
                    >
                      <span className="font-medium text-gray-700">{serviceName}</span>
                      {customerName && (
                        <span className="text-gray-400 ml-1">{customerName}</span>
                      )}
                    </div>
                  );
                })}
              </button>
            );
          })}
        </div>
      </div>

      {/* Month grid fills full width — no staging column in month view */}
    </div>
  );
};

export default MonthSchedulerView;
